import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Checkbox, FormControlLabel, Alert, CircularProgress,
} from "@mui/material";
import legalContent from "./legalContent";
import { useUser } from "../users/providers/UserProvider";
import { useDismissible } from "../native/dismissibleStack";
import { getMyConsent, acceptTerms } from "../users/service/userApi";
import { claimRefusals } from "../utils/refusalNotice";
import PolicyUpdateGate from "./PolicyUpdateGate";

/**
 * Whether the account owes a RE-acceptance this build can ask for (Q-20).
 *
 * Only on the server's explicit `outdated: true`: an older server never sends
 * it and has no way to record a newer version, so a screen raised from the
 * version strings alone could never be accepted. And only when the server's
 * current version is the one bundled here: otherwise the summary and the
 * documents this build would show describe a different text from the one the
 * acceptance would be recorded against. Either way the answer is "do not
 * block", and the next launch asks again.
 */
const needsReacceptance = (status) =>
  !status?.required &&
  status?.outdated === true &&
  status?.currentPolicyVersion === legalContent.version;

/**
 * Whether the first-run acceptance this build can give would be recorded.
 *
 * The screen sends the version it shows, and the server records only the
 * current one, refusing any other (C1 of the Q-20 review). When the server
 * says its current version is a different one, accepting here could only fail,
 * and this screen has no Sign out, so blocking would lock the account out of
 * the app. It steps aside instead; the account still owes the acceptance and a
 * build carrying the current text asks for it. A server that names no current
 * version predates the check and records whatever it records, so the screen
 * shows as it always has.
 */
const canRecordFirstAcceptance = (status) =>
  !status?.currentPolicyVersion || status.currentPolicyVersion === legalContent.version;

/**
 * The acceptance an OAuth account never gave.
 *
 * Email/password signup has blocked submit until the box is ticked since #65,
 * so accounts created that way arrive with consent on file. Two populations do
 * not: Google sign-in never touches that form (and `syncSupabaseSession`
 * deliberately records nothing rather than fabricating an acceptance), and
 * every account created before consent shipped on 2026-08-03 predates the field
 * entirely — there was no backfill, and inventing one would have been a lie.
 * This is where both give it. (So does a sign-up whose form showed a version
 * other than the server's current one: the bridge records nothing for it.)
 *
 * So the copy must NOT say how you signed in. It said `You signed in with
 * Google`, which is false for the second group, and they are the older accounts
 * — the owner and the first testers.
 *
 * Deliberately NOT dismissible. Android Back is claimed and swallowed while the
 * gate is up: there is nowhere to go behind it, and letting Back fall through
 * would either drop the user into the app without consent or quit the app on a
 * reflex press. The policy dialog opened from inside it IS dismissible, so Back
 * closes the document and returns here.
 *
 * It is also where an account that accepted an OLDER version is asked to accept
 * the current one (Q-20, PolicyUpdateGate). Both come from the one consent
 * check below, and the first-run screen wins, so a brand-new account never sees
 * two consent screens in a row.
 */
const FirstRunConsentGate = () => {
  const { user } = useUser();
  const [required, setRequired] = useState(false);
  const [outdated, setOutdated] = useState(false);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [policyKey, setPolicyKey] = useState(null);

  useEffect(() => {
    if (!user) { setRequired(false); setOutdated(false); return undefined; }
    let cancelled = false;
    getMyConsent()
      .then((r) => {
        if (cancelled) return;
        setRequired(Boolean(r?.required) && canRecordFirstAcceptance(r));
        setOutdated(needsReacceptance(r));
      })
      // A failed check must not lock anyone out of their own app. The account
      // still owes an acceptance and will be asked again next launch.
      .catch(() => { if (!cancelled) { setRequired(false); setOutdated(false); } });
    return () => { cancelled = true; };
  }, [user]);

  const closePolicy = useCallback(() => setPolicyKey(null), []);
  useDismissible(Boolean(policyKey), closePolicy, "first-run-policy-dialog");
  // Claim Back for the gate itself and do nothing with it.
  useDismissible(required && !policyKey, () => {}, "first-run-consent-gate");

  const accept = async () => {
    if (!checked || saving) return;
    setSaving(true);
    setError(null);
    try {
      // The version this screen's documents carry (C1 of the Q-20 review).
      // Without it the server records the older 2026-09-26, the newest text a
      // build that names nothing could have shown, and asks again.
      await acceptTerms(legalContent.version);
      setRequired(false);
      // The acceptance just recorded is of the current version, so there is
      // nothing left to re-accept: never a second screen straight after this.
      setOutdated(false);
    } catch (e) {
      // Shown inside the consent dialog (B-31).
      claimRefusals();
      setError(typeof e === "string" ? e : e?.message || "Could not record your acceptance");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;
  if (!required) {
    return outdated ? <PolicyUpdateGate onAccepted={() => setOutdated(false)} /> : null;
  }

  const policyDoc = policyKey ? legalContent[policyKey] : null;
  const openPolicy = (key) => (e) => {
    // Leave a modified click alone so the full page can still open in a new tab.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    setPolicyKey(key);
  };

  return (
    <>
      <Dialog
        open
        fullScreen
        disableEscapeKeyDown
        data-testid="first-run-consent-gate"
        PaperProps={{
          sx: {
            // The accept control must clear the gesture bar; at API 36
            // edge-to-edge is enforced with no opt-out.
            p: 3,
            pb: "calc(24px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))",
            pt: "calc(24px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 2,
            maxWidth: 520,
            mx: "auto",
          },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 900 }}>
          Before you start
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We do not have your agreement to these on file yet. They cover trading,
          meeting people in person, and how your location is used.
        </Typography>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        <FormControlLabel
          sx={{ alignItems: "flex-start", mt: 1 }}
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              inputProps={{ "data-testid": "first-run-consent-checkbox" }}
              sx={{ pt: 0 }}
            />
          }
          label={
            <Typography variant="body2">
              I am 18 or over and I agree to the{" "}
              <a href="/terms" onClick={openPolicy("terms")} data-testid="first-run-terms-link">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" onClick={openPolicy("privacy")} data-testid="first-run-privacy-link">
                Privacy Policy
              </a>
              .
            </Typography>
          }
        />

        <Button
          variant="contained"
          size="large"
          disabled={!checked || saving}
          onClick={accept}
          data-testid="first-run-consent-accept"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Agree and continue
        </Button>

        <Typography variant="caption" color="text.secondary">
          You can read both documents in full from Settings at any time.
        </Typography>
      </Dialog>

      <Dialog open={Boolean(policyDoc)} onClose={closePolicy} scroll="paper" fullWidth maxWidth="sm">
        <DialogTitle>{policyDoc?.title}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.6 }}>
            {policyDoc?.body}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePolicy} data-testid="first-run-policy-close">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FirstRunConsentGate;
